import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


export function EBJwtCheckPanel() {
  const [loading, setLoading] = useState(false);
  const [environment, setEnvironment] = useState('sandbox');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/eb-jwt-check', {
        params: { environment },
      });
      setResult(data);
      if (data.pass) {
        toast.success(`JWT check passed for ${environment}`);
      } else {
        toast.error(`JWT check failed for ${environment}`);
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      // Also capture HTTP status if available
      setResult({
        pass: false,
        http_status: err.response?.status || null,
        reason: msg,
      });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Enable Banking JWT Check
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Verify that the stored PEM key can sign a valid JWT accepted by the Enable Banking API.
          Runs a lightweight auth probe against the selected environment.
        </p>

        {/* Environment selector + run button */}
        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Environment</label>
            <select
              className="border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>

          <div className="pt-4">
            <Button
              size="sm"
              onClick={runCheck}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Run Check
            </Button>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Running JWT check against {environment}...
          </div>
        )}

        {/* Result */}
        {!loading && result && (
          <div className="border rounded-lg p-4 space-y-3">
            {/* Pass/fail badge */}
            <div className="flex items-center gap-3">
              {result.pass ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <Badge
                variant={result.pass ? 'default' : 'destructive'}
                className={result.pass ? 'bg-green-600 hover:bg-green-700 text-[11px]' : 'text-[11px]'}
              >
                {result.pass ? 'PASS' : 'FAIL'}
              </Badge>
              {result.http_status && (
                <span className="text-xs text-muted-foreground">
                  HTTP {result.http_status}
                </span>
              )}
            </div>

            {/* Error reason */}
            {!result.pass && (result.error || result.reason) && (
              <div className="flex items-start gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="text-xs">{result.error || result.reason}</span>
              </div>
            )}

            {/* Extra detail from the API response */}
            {result.detail && (
              <div className="rounded border px-3 py-2 bg-muted/20">
                <p className="text-xs font-medium text-muted-foreground mb-1">Detail</p>
                <p className="text-xs">{typeof result.detail === 'string' ? result.detail : JSON.stringify(result.detail)}</p>
              </div>
            )}

            {result.environment && (
              <p className="text-xs text-muted-foreground">
                Checked environment: <span className="font-medium">{result.environment}</span>
              </p>
            )}

            {result.checked_at && (
              <p className="text-[10px] text-muted-foreground text-right">
                Checked at {new Date(result.checked_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Error without result */}
        {!loading && error && !result && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
